/**
 * CAOS-689 — mentions, on the writing side: spotting one being typed, and writing one down.
 *
 * Shared code, not a component — it registers no custom element. Pure functions over a string and
 * a caret position, so the rules can be tested as rules. A trigger that could only be tested by
 * driving a textarea would be tested by nobody.
 *
 * ── THE SAME GRAMMAR, WRITTEN TWICE, HELD TOGETHER BY A FIXTURE ───────────────────────────────
 * The kernel parses these too (`kernel/src/mentions/mention-text.ts`). This library ships with
 * ZERO runtime dependencies and shares no code with the kernel — a property worth more than the
 * convenience of one implementation — so the grammar exists in both places, and both suites read
 * the same fixture (`kernel/src/mentions/mention-grammar.fixture.json`) and assert the same inputs
 * give the same tokens. Two parsers that agree because a test says so is a different thing from
 * two that happen to agree today.
 *
 * If you change the shape, change the fixture first. A case only one side satisfies is a red on
 * that side, which is the whole point.
 *
 * ── WHAT IS STORED IS A REFERENCE, NOT A NAME ─────────────────────────────────────────────────
 * `@Morgan` is what somebody types; it is not what is kept. Names are shared, and people are
 * renamed. The id is the claim and the label is the courtesy — see the kernel module's header for
 * the whole argument, which is the same argument on both sides.
 */

/** The character that opens a mention as somebody types it. */
export const MENTION_TRIGGER = '@';

/** What a mention points at. */
export type MentionKind = 'person' | 'record';

/** A mention found in a body, with where it is. */
export interface MentionToken {
  kind: MentionKind;
  ref: string;
  label: string;
  raw: string;
  start: number;
  end: number;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const API_NAME = /^[a-z][a-z0-9_]*(__[a-z][a-z0-9_]*)?$/;
const MENTION_SOURCE = '<@(person|record):([^|>\\s]+)\\|([^<>]*)>';

/** Is this a syntactically valid reference for this kind? */
export function isValidRef(kind: MentionKind, ref: string): boolean {
  if (kind === 'person') return UUID.test(ref);
  const slash = ref.indexOf('/');
  if (slash <= 0) return false;
  return API_NAME.test(ref.slice(0, slash)) && UUID.test(ref.slice(slash + 1));
}

/**
 * Every mention in a body, in the order it appears. A token whose reference is not well formed is
 * not a mention — it stays in the text as the characters somebody typed.
 *
 * The regex is built per call rather than held at module scope: a shared `/g` regex carries
 * `lastIndex` between calls, so the second caller reads from wherever the first one stopped.
 */
export function parseMentions(source: string): MentionToken[] {
  const re = new RegExp(MENTION_SOURCE, 'g');
  const out: MentionToken[] = [];
  for (let m = re.exec(source); m !== null; m = re.exec(source)) {
    const kind = m[1] as MentionKind;
    const ref = m[2] ?? '';
    if (!isValidRef(kind, ref)) continue;
    out.push({ kind, ref, label: m[3] ?? '', raw: m[0], start: m.index, end: m.index + m[0].length });
  }
  return out;
}

/** Write a mention down. The one place the shape is produced, so it cannot drift from the reader. */
export function formatMention(kind: MentionKind, ref: string, label: string): string {
  if (!isValidRef(kind, ref)) throw new Error(`not a ${kind} reference: ${ref}`);
  // Neither bracket may appear in a label: `>` ends the token, and `<` would let a mention written
  // inside another one's label swallow it. See the kernel module's header for the whole argument.
  const safe = label.replace(/[\r\n]+/g, ' ').replace(/[<>]/g, '').trim();
  return `<@${kind}:${ref}|${safe}>`;
}

/**
 * How long a half-typed mention may get before it stops being one.
 *
 * Without a ceiling, one stray `@` leaves the picker open for the rest of the message, over a
 * query nobody is typing. Spaces are allowed up to {@link MENTION_QUERY_MAX_WORDS} because people
 * have surnames and type them.
 */
export const MENTION_QUERY_MAX_LENGTH = 50;
export const MENTION_QUERY_MAX_WORDS = 2;

/** A mention being typed: where it starts, and what has been typed so far. */
export interface ActiveMention {
  /** Index of the `@`. */
  start: number;
  /** Index one past the last character typed — the caret. */
  end: number;
  /** What has been typed after the `@`. May be empty: a bare `@` opens the picker. */
  query: string;
}

/**
 * The mention being typed at `caret`, or null.
 *
 * The `@` must open a word — start of text, or after whitespace or an opening bracket. That one
 * rule is what stops `someone@example.test` from opening a picker in the middle of an email
 * address, which is the single most common way this feature annoys people.
 *
 * Searching backwards from the caret rather than forwards from the start, because the question is
 * about the caret: a body may hold several finished mentions and the one being typed is the only
 * one that matters here.
 */
export function activeMentionQuery(text: string, caret: number): ActiveMention | null {
  const at = Math.max(0, Math.min(caret, text.length));
  const before = text.slice(0, at);
  const trigger = before.lastIndexOf(MENTION_TRIGGER);
  if (trigger < 0) return null;

  const opensWord = trigger === 0 || /[\s(\[{]/.test(before[trigger - 1] ?? '');
  if (!opensWord) return null;

  const query = before.slice(trigger + 1);
  if (query.length > MENTION_QUERY_MAX_LENGTH) return null;
  // A newline ends it: a mention does not span lines, and Shift+Enter is how people start one.
  if (/[\r\n]/.test(query)) return null;
  // `>` would mean the caret is inside or past a finished mention, not typing a new one.
  if (query.includes('>')) return null;
  if (query.trim().split(/\s+/).filter(Boolean).length > MENTION_QUERY_MAX_WORDS) return null;

  return { start: trigger, end: at, query };
}

/** The text and caret after choosing a candidate for the mention being typed. */
export interface MentionInsertion {
  text: string;
  caret: number;
}

/**
 * Replace the mention being typed with a written-down one.
 *
 * A trailing space is added, because the next thing somebody types is the rest of the sentence and
 * a mention butting straight against a word reads as one word. Returns the text unchanged when
 * nothing is being typed at `caret` — a chosen candidate with no query to replace would otherwise
 * be inserted at a position nobody put the caret in.
 */
export function insertMention(
  text: string,
  caret: number,
  choice: { kind: MentionKind; ref: string; label: string },
): MentionInsertion {
  const active = activeMentionQuery(text, caret);
  if (!active) return { text, caret };
  const token = `${formatMention(choice.kind, choice.ref, choice.label)} `;
  const next = text.slice(0, active.start) + token + text.slice(active.end);
  return { text: next, caret: active.start + token.length };
}
